import { EMBED_IMAGE_CONTAINER_CLASS } from './constants';
import uploader from '~/components/utils/uploader';
import debounce from '~/components/utils/debounce';

const MAX_IMAGE_WIDTH = 664;
const MAX_IMAGE_HEIGHT = 500;
const CLOUDINARY_FETCH_URL = 'https://res.cloudinary.com/bravado/image/fetch';
const EDITOR_ACTIONS_STICKY_CLASS = 'community-text-editor__actions--sticky';

export const getActiveStorageUrl = fileName => {
  return `${process.env.ACTIVE_STORAGE_URL}/active_storage/${fileName}`;
};

const getImageUrl = (fileName, width, height) => {
  return `${CLOUDINARY_FETCH_URL}/${width ? `w_${width},` : ``}${
    height ? `h_${height},` : ``
  }f_auto,q_auto/${getActiveStorageUrl(fileName)}`;
};

const getImageSrcSet = (fileName, baseWidth, height) => {
  return `${getImageUrl(fileName, baseWidth * 2, height * 2)} 2x, ${getImageUrl(fileName, baseWidth, height)} 1x`;
};

export const getImageSize = file => {
  return new Promise(resolve => {
    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      resolve({ width: image.width, height: image.height });
    };
  });
};

export const getImageSizeMb = file => parseFloat((file.size / 1024 ** 2).toFixed(2));

export const getImageInfo = async (file, isMobile, editorWidth) => {
  const { width, height } = await getImageSize(file);

  const aspectRatio = width / height;
  const isVertical = aspectRatio < 1;

  const heightScaledToEditorSize = width > editorWidth ? Math.round(height / (width / editorWidth)) : height;

  const containerHeight =
    isVertical && height > MAX_IMAGE_HEIGHT && !isMobile ? MAX_IMAGE_HEIGHT : heightScaledToEditorSize;

  return { width, height, isVertical, containerHeight };
};

const getBlotById = (blotId, quill, Quill) => {
  const imageNode = quill.container.querySelector(`div.${EMBED_IMAGE_CONTAINER_CLASS}[data-blot-id="${blotId}"]`);
  return Quill.find(imageNode);
};

export const insertBlotToEditor = (imageInfo, blotId, quill, Quill) => {
  const cursorIndex = quill.getSelection().index;

  quill.insertEmbed(
    cursorIndex,
    'BravadoImageBlot',
    {
      blotId,
      containerHeight: imageInfo.containerHeight,
    },
    'user',
  );
  quill.setSelection(cursorIndex + 2);

  return getBlotById(blotId, quill, Quill);
};

export const uploadImageToAS = async (file, token) => {
  const { key: fileName } = await uploader.upload({ file, token });

  return fileName;
};

export const updateImageBlot = (fileName, imageBlot, imageInfo) => {
  const { isVertical } = imageInfo;

  const imageWidth = isVertical ? null : MAX_IMAGE_WIDTH;
  const imageHeight = isVertical ? MAX_IMAGE_HEIGHT : null;

  return imageBlot.setImage({
    src: getImageUrl(fileName, imageWidth, imageHeight),
    srcSet: getImageSrcSet(fileName, imageWidth, imageHeight),
    originalUrl: getActiveStorageUrl(fileName),
  });
};

export const trackScroll = (actionsNode, editorAreaNode) => {
  const LINE_HEIGHT = 80;

  const actionsYPosition = actionsNode.getBoundingClientRect().top + window.pageYOffset;
  const editorStyles = getComputedStyle(editorAreaNode);
  const editorPadding = `${actionsNode.offsetHeight}px`;
  const editorNode = editorAreaNode.parentNode;

  const stickToolbar = () => {
    if (
      window.pageYOffset < actionsYPosition ||
      editorAreaNode.getBoundingClientRect().bottom < parseInt(actionsNode.offsetHeight) + LINE_HEIGHT
    ) {
      actionsNode.classList.remove(EDITOR_ACTIONS_STICKY_CLASS);
      editorNode.style.paddingTop = '';
      actionsNode.style.width = '';
      return;
    }

    actionsNode.classList.add(EDITOR_ACTIONS_STICKY_CLASS);
    editorNode.style.paddingTop = editorPadding;
    actionsNode.style.width = editorStyles.width;
  };

  return debounce(stickToolbar, 100);
};
